import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  REAL_APPROVED: "bg-emerald-100 text-emerald-800",
  REAL_NOT_APPROVED: "bg-sky-100 text-sky-800",
  DEMO_NOT_FOR_PRICING: "bg-amber-100 text-amber-800",
  MISSING: "bg-red-100 text-red-800",
};

const PHASE_STYLE = { locked: "bg-emerald-100 text-emerald-800", draft: "bg-amber-100 text-amber-800", empty: "bg-zinc-100 text-zinc-500" };

const ParamRow = ({ r, onEdit, onApprove, canApprove }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  return (
    <tr className="border-t border-border align-top text-xs" data-testid={`param-row-${r.path}`}>
      <td className="py-1 pr-2 font-mono max-w-[240px]">{r.path}</td>
      <td className="pr-2">{r.value === null ? "—" : String(r.value)} {r.unit}</td>
      <td className="pr-2">{r.operation}</td>
      <td className="pr-2 max-w-[180px] text-muted-foreground">{r.source}</td>
      <td className="pr-2">
        <Badge className={`text-[10px] ${STATUS_STYLE[r.status]}`} data-testid={`param-status-${r.path}`}>{r.status}</Badge>
        {r.approved_by && <span className="block text-[10px] text-muted-foreground">{r.approved_by}</span>}
      </td>
      <td className="pr-2 max-w-[220px] text-muted-foreground">{r.blocked_reason || "—"}</td>
      <td className="whitespace-nowrap">
        {editing ? (
          <span className="flex gap-1">
            <Input className="h-6 w-20 text-xs" value={val} onChange={(e) => setVal(e.target.value)}
              data-testid={`param-input-${r.path}`} />
            <Button size="sm" className="h-6 px-2" data-testid={`param-save-${r.path}`}
              onClick={() => { onEdit(r.path, val); setEditing(false); }}>OK</Button>
          </span>
        ) : (
          <span className="flex gap-1">
            <Button size="sm" variant="outline" className="h-6 px-2" data-testid={`param-edit-${r.path}`}
              onClick={() => { setVal(String(r.value ?? "")); setEditing(true); }}>Modifica</Button>
            {canApprove && r.status !== "REAL_APPROVED" && r.value !== null && (
              <Button size="sm" variant="outline" className="h-6 px-2" title="Approva come responsabile"
                data-testid={`param-approve-${r.path}`} onClick={() => onApprove(r.path)}>
                <Check size={12} />
              </Button>
            )}
          </span>
        )}
      </td>
    </tr>
  );
};

export default function ShopCalibration() {
  const [matrix, setMatrix] = useState(null);
  const [cases, setCases] = useState(null);
  const [agg, setAgg] = useState(null);
  const [tab, setTab] = useState("params");

  const load = useCallback(async () => {
    try {
      const [m, c, a] = await Promise.all([
        api.get("/shop-profile/matrix"), api.get("/calibration/cases"),
        api.get("/calibration/blind-baseline/aggregate")]);
      setMatrix(m.data); setCases(c.data); setAgg(a.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onEdit = async (path, value) => {
    try {
      const num = Number(value);
      await api.put("/shop-profile", { updates: { [path]: { value: isNaN(num) ? value : num, demo: false, source: "inserito_da_officina" } } });
      toast.success("Valore aggiornato (nuova versione) — ricordare l'approvazione");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  const onApprove = async (path) => {
    try {
      await api.post("/shop-profile/approve", { paths: [path] });
      toast.success("Parametro approvato REAL_APPROVED");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore approvazione"); }
  };

  if (!matrix || !cases) return <div className="p-8 text-sm" data-testid="calibration-loading">Caricamento calibrazione…</div>;

  const counts = matrix.counts || {};
  const total = matrix.rows.length;
  const approved = counts.REAL_APPROVED || 0;

  return (
    <div className="p-4 space-y-4 max-w-6xl" data-testid="shop-calibration-page">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-semibold">Calibrazione officina (P0.1)</h1>
        <Button size="sm" variant="outline" onClick={load} data-testid="calibration-reload-btn">
          <RefreshCw size={13} className="mr-1" /> Aggiorna
        </Button>
      </div>

      <div className="p-3 text-sm border bg-amber-50 border-amber-300 text-amber-900" data-testid="calibration-status-banner">
        <strong>{matrix.status}</strong>
        <p className="text-xs mt-1">
          Parametri REAL_APPROVED: {approved}/{total} · demo: {counts.DEMO_NOT_FOR_PRICING || 0} · reali non approvati: {counts.REAL_NOT_APPROVED || 0} · mancanti: {counts.MISSING || 0}.
          Il preventivo definitivo resta bloccato finché ogni parametro usato non è REAL_APPROVED. Nessun dato viene simulato.
        </p>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant={tab === "params" ? "default" : "outline"} data-testid="tab-params" onClick={() => setTab("params")}>
          Dati officina ({approved}/{total})
        </Button>
        <Button size="sm" variant={tab === "cases" ? "default" : "outline"} data-testid="tab-cases" onClick={() => setTab("cases")}>
          Casi PDF da approvare ({cases.cases.length})
        </Button>
        <Button size="sm" variant={tab === "blind" ? "default" : "outline"} data-testid="tab-blind" onClick={() => setTab("blind")}>
          Prova cieca — riepilogo ({agg?.casi?.length || 0})
        </Button>
      </div>

      {tab === "params" && (
        <Card data-testid="param-matrix-card">
          <CardHeader className="py-3"><CardTitle className="text-sm">Matrice parametri (profilo v{matrix.profile_version})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="text-left text-muted-foreground text-xs">
                <th className="py-1 pr-2">Parametro</th><th className="pr-2">Valore</th><th className="pr-2">Operazione</th>
                <th className="pr-2">Fonte</th><th className="pr-2">Stato</th><th className="pr-2">Motivo blocco</th><th>Azioni</th>
              </tr></thead>
              <tbody>
                {matrix.rows.map((r) => (
                  <ParamRow key={r.path} r={r} onEdit={onEdit} onApprove={onApprove} canApprove />
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {tab === "cases" && (
        <Card data-testid="calibration-cases-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">
              20 PDF DEV — ordine consigliato (dal più semplice). FASE A cieca: solo disegno, niente QuoteOS.
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">#</th><th className="pr-2">Caso</th><th className="pr-2">PDF</th>
                <th className="pr-2">Stima tempo</th><th className="pr-2">A</th><th className="pr-2">B</th>
                <th className="pr-2">C</th><th className="pr-2">D</th><th className="pr-2">Differenze</th><th></th>
              </tr></thead>
              <tbody>
                {cases.cases.map((c) => (
                  <tr key={c.case_id} className="border-t border-border" data-testid={`case-row-${c.case_id}`}>
                    <td className="py-1 pr-2">{c.order}</td>
                    <td className="pr-2 font-mono">{c.case_id}</td>
                    <td className="pr-2 max-w-[220px] truncate">{c.pdf_name}</td>
                    <td className="pr-2">~{c.est_minutes} min*</td>
                    {["A", "B", "C", "D"].map((ph) => (
                      <td key={ph} className="pr-2">
                        <Badge className={`text-[10px] ${PHASE_STYLE[c.phases[ph]]}`} data-testid={`case-${c.case_id}-phase-${ph}`}>{c.phases[ph]}</Badge>
                      </td>
                    ))}
                    <td className="pr-2">{c.differences_count}</td>
                    <td>
                      <Link to={`/calibration/${c.case_id}`} className="text-xs underline" data-testid={`open-case-${c.case_id}`}>
                        Apri
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground mt-2">* stima organizzativa, non misurata.</p>
          </CardContent>
        </Card>
      )}
      {tab === "blind" && agg && (
        <Card data-testid="blind-aggregate-card">
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Prova cieca — riepilogo aggregato ({agg.casi.length} casi con confronto)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 overflow-x-auto">
            <p className="text-[11px] text-amber-800 border border-amber-200 bg-amber-50 p-1.5" data-testid="blind-aggregate-note">{agg.aggregato.nota}</p>
            <table className="w-full text-xs" data-testid="blind-aggregate-cases">
              <thead><tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">Caso</th><th className="pr-2">Hash output</th><th className="pr-2">Classificazioni</th>
                <th className="pr-2">Errori da preventivo</th><th>Gate</th>
              </tr></thead>
              <tbody>
                {agg.casi.map((c) => (
                  <tr key={c.case_id} className="border-t border-border" data-testid={`blind-case-row-${c.case_id}`}>
                    <td className="py-1 pr-2 font-mono">
                      <Link to={`/calibration/${c.case_id}`} className="underline">{c.case_id}</Link>
                    </td>
                    <td className="pr-2 font-mono" title={c.output_sha256}>{(c.output_sha256 || "").slice(0, 12)}…</td>
                    <td className="pr-2">{Object.entries(c.classificazioni).map(([k, v]) => `${k}:${v}`).join(" · ")}</td>
                    <td className="pr-2">{c.errori_da_preventivo}</td>
                    <td>{c.gate?.price_status}{c.gate?.bloccato ? " (bloccato)" : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <table className="w-full text-xs" data-testid="blind-aggregate-table">
              <thead><tr className="text-left text-muted-foreground">
                <th className="py-1 pr-2">Categoria</th><th className="pr-2">Presente</th><th className="pr-2">Corretti</th>
                <th className="pr-2">Mancati</th><th className="pr-2">Errati</th><th className="pr-2">Falsi pos.</th>
                <th className="pr-2">Duplicati</th><th className="pr-2">Ambigui</th><th className="pr-2">Review</th>
                <th className="pr-2">Precision</th><th>Recall</th>
              </tr></thead>
              <tbody>
                {(agg.aggregato.per_categoria || []).map((r) => (
                  <tr key={r.categoria} className="border-t border-border" data-testid={`agg-cat-${r.categoria}`}>
                    <td className="py-1 pr-2 font-medium">{r.categoria}</td>
                    <td className="pr-2">{r.presente_nel_disegno}</td><td className="pr-2 text-emerald-700">{r.corretti}</td>
                    <td className="pr-2 text-red-700">{r.mancati}</td><td className="pr-2 text-rose-700">{r.errati}</td>
                    <td className="pr-2 text-orange-700">{r.falsi_positivi}</td><td className="pr-2 text-purple-700">{r.duplicati}</td>
                    <td className="pr-2 text-amber-700">{r.ambigui}</td><td className="pr-2 text-sky-700">{r.review}</td>
                    <td className="pr-2">{r.precision ?? "n/d"}</td><td>{r.recall ?? "n/d"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-muted-foreground">
              Classificazioni totali: {Object.entries(agg.aggregato.per_classificazione || {}).filter(([, v]) => v > 0).map(([k, v]) => `${k}: ${v}`).join(" · ")}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
